import React from "react";
import { UI_CAPTION_DF } from "oolib/dist/v2/components/Typo2";
import { colors2, UI_CAPTION_SEMIBOLD_DF } from "oolib";
import { dataVizColorsLight } from "@/components/dataviz/common/colors";

/**
 * Renders a numeric value sitting on top of a horizontal fill-bar whose width
 * is `value / maxCount`. Visually conveys ranking density at a glance —
 * matches the "Count" column pattern from analytics tables.
 *
 * Pass `maxCount` via colConfig.props at table-config build time; it's the
 * denominator for the bar width. If omitted or 0, the cell renders the
 * number only (no bar).
 *
 * `colorIdx` picks the bar tint from `dataVizColorsLight` (palette wraps via
 * mod, same as BarChart). Defaults to 5 (light blue). `color` overrides it
 * outright (a per-row accent via dynamicPropsCallback).
 *
 * `display` replaces the printed number (the bar still scales on `value`) —
 * for a score whose text form isn't the raw number ("0.42").
 *
 * `compact` is the slim reading for dense tables: a fixed 50px, 6px-high
 * track first (so every row's bar lines up), caption-sized text after it —
 * instead of the number over a 24px bar.
 */
const TableCountBar = ({ value, maxCount, colorIdx = 5, color, display, compact = false }) => {
  if (value === undefined || value === null) return null;

  const numericValue = Number(value) || 0;
  const numericMax = Number(maxCount) || 0;
  const pct =
    numericMax > 0 ? Math.max(0, Math.min(100, (numericValue / numericMax) * 100)) : 0;
  const fill = color || dataVizColorsLight[colorIdx % dataVizColorsLight.length];

  if (compact) {
    return (
      <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", width: "100%", minWidth: 0 }}>
        <div style={{ width: "50px", flexShrink: 0, height: "6px", borderRadius: "3px", background: colors2.grey10, overflow: "hidden" }}>
          <div aria-hidden style={{ width: `${pct}%`, height: "100%", borderRadius: "3px", background: fill }} />
        </div>
        <UI_CAPTION_DF style={{ color: colors2.grey80, fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" }}>
          {display ?? numericValue}
        </UI_CAPTION_DF>
      </div>
    );
  }

  return (
    <div
      style={{
        position: "relative",
        display: "flex",
        alignItems: "center",
        width: "100%",
        height: "100%",
        padding: "0.2rem 0.6rem",
        boxSizing: "border-box",
      }}
    >
      <div
        aria-hidden
        style={{
          position: "absolute",
          left: 0,
          top: "50%",
          transform: "translateY(-50%)",
          width: `${pct}%`,
          // BarChart's _base_barchart_ui_configs.barHeight + .barRadius —
          // mirroring them here keeps the table-cell bar visually consistent
          // with the BarChart bars rendered in the Prevalence view.
          height: "24px",
          background: fill,
          borderRadius: "4px",
        }}
      />
      <UI_CAPTION_SEMIBOLD_DF style={{ position: "relative", zIndex: 1, color: colors2.grey80 }}>
        {display ?? numericValue}
      </UI_CAPTION_SEMIBOLD_DF>
    </div>
  );
};

export default TableCountBar;
